import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useContactModal } from '../context/ContactModalContext';
import '../styles/components/SiteChrome.css';

const SECTION_LINKS = [
  { id: 'about', label: 'About' },
  { id: 'what', label: 'What we build' },
  { id: 'how', label: 'How we work' },
];

/** Probe just below the floating pill to decide light vs dark chrome. */
function readNavTone() {
  const probeY = 96;
  const nodes = document.querySelectorAll('[data-nav-tone]');
  let tone = 'light';
  nodes.forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.top <= probeY && r.bottom > probeY) {
      tone = el.getAttribute('data-nav-tone') === 'dark' ? 'dark' : 'light';
    }
  });
  return tone;
}

function SiteNav() {
  const [open, setOpen] = useState(false);
  const [tone, setTone] = useState('light');
  const location = useLocation();
  const navigate = useNavigate();
  const { openContactModal } = useContactModal();

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Close the overlay whenever the route changes
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let frame = 0;
    const sync = () => {
      frame = 0;
      setTone(readNavTone());
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(sync);
    };

    sync();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [location.pathname, location.hash]);

  const goToSection = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === '/') {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(`/#${id}`);
    }
  };

  const openTalk = (e) => {
    e.preventDefault();
    setOpen(false);
    openContactModal('demo');
  };

  const navClass = [
    'st-nav',
    open ? 'st-nav--open' : '',
    tone === 'dark' ? 'st-nav--dark' : 'st-nav--light',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <header className={navClass}>
      <div className="st-nav-inner">
        <Link to="/" className="st-wordmark" onClick={() => setOpen(false)}>
          <img src="/logo.png" alt="" className="st-wordmark-glyph" />
          <span className="st-wordmark-text">BloomNav</span>
        </Link>

        <nav className="st-nav-links" aria-label="Primary">
          {SECTION_LINKS.map((s) => (
            <a key={s.id} href={`/#${s.id}`} className="st-nav-link" onClick={goToSection(s.id)}>
              {s.label}
            </a>
          ))}
          <Link
            to="/products"
            className={`st-nav-link${location.pathname === '/products' ? ' is-active' : ''}`}
          >
            Products
          </Link>
          <Link
            to="/contact"
            className={`st-nav-link${location.pathname === '/contact' ? ' is-active' : ''}`}
          >
            Contact
          </Link>
        </nav>

        <div className="st-nav-right">
          <button type="button" className="st-nav-cta" onClick={openTalk}>
            Let&rsquo;s talk
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 13 13 3M6 3h7v7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" />
            </svg>
          </button>
          <button
            type="button"
            className="st-nav-burger"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <span />
            <span />
          </button>
        </div>
      </div>

      <div className="st-nav-overlay" aria-hidden={!open}>
        <nav className="st-overlay-links" aria-label="Mobile">
          {SECTION_LINKS.map((s) => (
            <a key={s.id} href={`/#${s.id}`} className="st-overlay-link" onClick={goToSection(s.id)}>
              {s.label}
            </a>
          ))}
          <Link to="/pricing" className="st-overlay-link" onClick={() => setOpen(false)}>
            Pricing
          </Link>
          <Link to="/products" className="st-overlay-link" onClick={() => setOpen(false)}>
            Products
          </Link>
          <Link to="/contact" className="st-overlay-link" onClick={() => setOpen(false)}>
            Contact
          </Link>
        </nav>
        <div className="st-overlay-foot">
          <button type="button" className="st-nav-cta st-nav-cta--wide" onClick={openTalk}>
            Let&rsquo;s talk
          </button>
          <p>Starting at $16,000</p>
        </div>
      </div>
    </header>
  );
}

export default SiteNav;
